import React from 'react'

interface Props { children: React.ReactNode; name?: string }
interface State { error: Error | null }

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error) { return { error } }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[ErrorBoundary:${this.props.name ?? 'unknown'}]`, error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 12, padding: 24, height: '100%',
          background: 'var(--bg-1)', color: 'var(--text-1)',
          fontFamily: 'var(--font-mono)', fontSize: 12,
        }}>
          <div style={{ fontSize: 28 }}>💥</div>
          <div style={{ fontWeight: 700, color: 'var(--red)', fontSize: 13 }}>
            {this.props.name ?? 'Component'} crashed
          </div>
          <div style={{ color: 'var(--text-2)', maxWidth: 320, textAlign: 'center', lineHeight: 1.6 }}>
            {this.state.error.message}
          </div>
          <button
            onClick={() => this.setState({ error: null })}
            style={{
              padding: '6px 16px', borderRadius: 6, border: '1px solid var(--accent)',
              background: 'rgba(124,110,245,0.1)', color: 'var(--accent)',
              cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11,
            }}
          >↻ Retry</button>
        </div>
      )
    }
    return this.props.children
  }
}

// Dev-only: expose a way to test error boundaries from the console
if (typeof window !== 'undefined') {
  (window as any).__testCrash = (name: string) => {
    window.dispatchEvent(new CustomEvent('pulse:test-crash', { detail: name }))
  }
}

/** Drop this inside an ErrorBoundary to test it. Listens for pulse:test-crash events. */
export function CrashTrigger({ name }: { name: string }) {
  const [crash, setCrash] = React.useState(false)
  React.useEffect(() => {
    const handler = (e: Event) => {
      if ((e as CustomEvent).detail === name) setCrash(true)
    }
    window.addEventListener('pulse:test-crash', handler)
    return () => window.removeEventListener('pulse:test-crash', handler)
  }, [name])
  if (crash) throw new Error(`Test crash in ${name}`)
  return null
}
